import React from 'react';
import LegalPage from '../../components/LegalPage/LegalPage';

const sections = [
   {
      heading: 'Introduction',
      content: [
         'Harsoft Solutions Private Limited ("Harsoft", "we", "our", or "us") respects your privacy and is committed to protecting your personal information.',
         'This Privacy Policy explains how we collect, use, store, share, and protect personal information when you visit our website, https://harsofts.com/, submit an enquiry, interact with our marketing campaigns, use our services, or communicate with us.',
         'By accessing our website or providing your information to us, you acknowledge this Privacy Policy.',
      ],
   },
   {
      heading: 'Information We Collect',
      content: [
         'Depending on how you interact with us, we may collect the following categories of information:',
         { sub: '2.1 Personal Information' },
         {
            list: [
               'Full name',
               'Email address',
               'Mobile or telephone number',
               'Company or organisation name',
               'Job title or professional information',
               'Any information you voluntarily provide through contact forms, enquiries, or communications',
            ],
         },
         { sub: '2.2 Business and Enquiry Information' },
         'When you enquire about our products or services, we may collect information about your business requirements, requested services, project details, and communications with our team.',
         { sub: '2.3 Technical Information' },
         'When you visit our website, certain technical information may be collected, including your IP address, browser type, device information, operating system, pages visited, referral source, and website usage data.',
         { sub: '2.4 Marketing and Campaign Information' },
         'When you interact with our advertising campaigns, landing pages, or promotional activities, we may receive information such as your contact details, campaign source, enquiry preferences, and interactions with our advertisements.',
         'The information collected depends on the forms you complete, your permissions, and the settings of the advertising platform.',
      ],
   },
   {
      heading: 'How We Use Your Information',
      content: [
         'We may use your personal information for the following purposes:',
         {
            list: [
               'To respond to enquiries and provide requested information.',
               'To communicate with you about our products, services, and business offerings.',
               'To prepare proposals, quotations, and service agreements.',
               'To manage customer relationships and business communications.',
               'To conduct marketing campaigns and assess campaign performance.',
               'To improve our website, services, user experience, and business operations.',
               'To maintain website security and prevent fraud, misuse, or unauthorised access.',
               'To comply with applicable legal, regulatory, and contractual obligations.',
               'To obtain feedback and improve our products and services.',
            ],
         },
         'We will process personal information for specified and lawful purposes, subject to applicable law and any required consent.',
      ],
   },
   {
      heading: 'Marketing Communications',
      paragraphs: [
         'If you provide your contact details through our website, enquiry forms, or marketing campaigns, we may contact you regarding your enquiry or requested services.',
         'We may also send promotional communications where permitted by applicable law and, where required, with your consent.',
         'You can opt out of promotional emails by following the unsubscribe instructions included in the message or by contacting us directly.',
         'Opting out of promotional communications will not necessarily prevent essential service-related or transactional communications.',
      ],
   },
   {
      heading: 'Cookies and Tracking Technologies',
      content: [
         'Our website may use cookies and similar technologies to:',
         {
            list: [
               'Ensure the website functions correctly.',
               'Remember preferences and improve usability.',
               'Understand website traffic and visitor behaviour.',
               'Measure the effectiveness of marketing campaigns.',
               'Support advertising measurement and optimisation, where enabled.',
            ],
         },
         'You can manage cookies through your browser settings and any consent tools provided on our website. Disabling certain cookies may affect website functionality.',
         'Where required, we will obtain consent before using non-essential cookies or tracking technologies.',
      ],
   },
   {
      heading: 'Advertising and Third-Party Platforms',
      paragraphs: [
         'We may use third-party advertising, analytics, and communication platforms to promote our services and measure campaign performance.',
         'Depending on the platforms we use, these may include services such as Google Ads, Meta Ads, website analytics providers, email service providers, and customer relationship management tools.',
         'These providers may process information according to their own privacy policies and the permissions and settings applicable to the relevant service.',
         'We do not sell your personal information in exchange for money. We share information with third parties only as appropriate for the purposes described in this policy, subject to applicable law.',
         'Please review the privacy policies of the relevant platforms for further information about their data practices.',
      ],
   },
   {
      heading: 'Sharing of Personal Information',
      content: [
         'We may share personal information with the following categories of recipients when necessary:',
         {
            list: [
               { label: 'Service providers', text: 'Website hosting, cloud storage, IT infrastructure, analytics, customer support, and communication providers.' },
               { label: 'Marketing partners', text: 'Advertising and campaign measurement providers, where applicable.' },
               { label: 'Business partners', text: 'Where necessary to deliver a requested service or respond to a business enquiry.' },
               { label: 'Professional advisers', text: 'Legal, accounting, auditing, and compliance advisers where appropriate.' },
               { label: 'Government and regulatory authorities', text: 'Where disclosure is required or permitted under applicable law.' },
               { label: 'Business successors', text: 'In connection with a merger, restructuring, acquisition, or transfer of business, subject to applicable legal requirements.' },
            ],
         },
         'We take reasonable steps to ensure that third parties handling information on our behalf are subject to appropriate confidentiality and data protection obligations.',
      ],
   },
   {
      heading: 'Data Retention',
      paragraphs: [
         'We retain personal information only for as long as reasonably necessary to fulfil the purposes for which it was collected, including responding to enquiries, maintaining business records, delivering services, resolving disputes, and meeting legal or contractual obligations.',
         'When information is no longer required, we will take appropriate steps to delete it, anonymise it, or otherwise dispose of it in accordance with applicable law and our retention practices.',
         'Retention periods may vary depending on the nature of the information and the purpose for which it is processed.',
      ],
   },
   {
      heading: 'Data Security',
      paragraphs: [
         'We take reasonable technical and organisational measures designed to protect personal information against unauthorised access, disclosure, alteration, loss, or misuse.',
         'These measures may include access controls, appropriate security practices, and safeguards for systems and service providers.',
         'However, no method of electronic transmission or storage is completely secure. We cannot guarantee absolute security, but we work to maintain appropriate protections for the information we handle.',
      ],
   },
   {
      heading: 'Your Privacy Rights and Choices',
      content: [
         'Depending on applicable law, you may have rights regarding your personal information, which may include:',
         {
            list: [
               'Requesting information about the personal data we process.',
               'Requesting correction of inaccurate or incomplete information.',
               'Requesting deletion of personal information where applicable.',
               'Withdrawing consent where processing is based on consent.',
               'Opting out of promotional communications.',
               'Raising a privacy concern or submitting a grievance.',
               'Exercising any other rights available under applicable data protection law.',
            ],
         },
         'To exercise an applicable right, please contact us using the details provided in the Contact Us section.',
         'We may need to verify your identity before responding to a request. We will handle requests in accordance with applicable law and explain if a request cannot be fulfilled.',
         'Withdrawing consent does not affect the lawfulness of processing carried out before the withdrawal.',
      ],
   },
   {
      heading: "Children's Privacy",
      paragraphs: [
         'Our website and business services are not intended to collect personal information from children without the appropriate legal basis, permissions, or safeguards.',
         'If you believe that a child has provided personal information to us in circumstances that are not permitted by applicable law, please contact us so that we can review the matter and take appropriate action.',
      ],
   },
   {
      heading: 'Third-Party Websites',
      paragraphs: [
         'Our website or communications may contain links to third-party websites, applications, or services.',
         'We do not control the privacy practices of these third parties and are not responsible for their content or policies. We encourage you to review their privacy policies before providing personal information.',
      ],
   },
   {
      heading: 'International Data Processing',
      paragraphs: [
         'Some of our service providers may process or store information in locations outside your state or country.',
         'Where personal information is transferred internationally, we will take steps required by applicable law and any applicable restrictions or safeguards governing such transfers.',
      ],
   },
   {
      heading: 'Changes to This Privacy Policy',
      paragraphs: [
         'We may update this Privacy Policy from time to time to reflect changes in our services, business practices, technologies, or applicable legal requirements.',
         'The revised version will be published on this page with an updated "Last Updated" date. We encourage you to review this page periodically.',
      ],
   },
];

const PrivacyPolicy = () => (
   <LegalPage
      title="Privacy Policy"
      description="Learn how Harsoft Solutions Private Limited collects, uses, shares and protects your personal information when you use our website and services."
      path="/privacy-policy"
      effective="9 October 2026"
      updated="9 October 2026"
      company="Harsoft Solutions Private Limited"
      intro={[]}
      sections={sections}
      note="This Privacy Policy is intended to provide general information about our privacy practices. It does not constitute legal advice."
   />
);

export default PrivacyPolicy;
